// PDF preview rendered with pdf.js onto canvases.
//
// Replaces an <iframe> with Chrome's built-in PDF viewer: that viewer applies
// its "fit to page" zoom (e.g. 67 %) to the whole tab, and Chrome stores the
// zoom per origin, so the entire intranet stayed zoomed out afterwards.
//
// pdf.js is loaded on demand, so it ends up in its own chunk and only pages
// with a preview download it.

let pdfjsPromise = null;

function loadPdfjs() {
    if (!pdfjsPromise) {
        pdfjsPromise = Promise.all([
            import('pdfjs-dist'),
            import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]).then(([pdfjs, worker]) => {
            pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
            return pdfjs;
        });
    }
    return pdfjsPromise;
}

export default (url) => {
    // Kept outside Alpine's reactive state: the proxy breaks pdf.js objects,
    // which use private class fields.
    let pdf = null;
    let renderedWidth = 0;
    let renderToken = 0;

    return {
        loading: true,
        error: '',

        async init() {
            try {
                const pdfjs = await loadPdfjs();
                // pdf.js resolves relative URLs against the page URL, not the <base> tag
                const absolute = new URL(url, document.baseURI).href;
                pdf = await pdfjs.getDocument({ url: absolute, withCredentials: true }).promise;
                await this.render();
            } catch (e) {
                this.error = 'Die PDF-Vorschau konnte nicht geladen werden.';
                console.error('[pdfPreview]', e);
            } finally {
                this.loading = false;
            }

            // Re-render when the pane is resized (narrow/wide toggle, window resize)
            let timer = null;
            new ResizeObserver(() => {
                clearTimeout(timer);
                timer = setTimeout(() => this.render(), 150);
            }).observe(this.$refs.pages);
        },

        async render() {
            if (!pdf) {
                return;
            }
            const container = this.$refs.pages;
            const width = container.clientWidth;
            if (!width || width === renderedWidth) {
                return;
            }
            renderedWidth = width;

            // A newer render (e.g. while resizing) supersedes this one
            const token = ++renderToken;
            const dpr = window.devicePixelRatio || 1;
            const canvases = [];

            for (let n = 1; n <= pdf.numPages; n++) {
                const page = await pdf.getPage(n);
                const base = page.getViewport({ scale: 1 });
                const cssScale = width / base.width;
                const viewport = page.getViewport({ scale: cssScale * dpr });

                const canvas = document.createElement('canvas');
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.width = '100%';
                canvas.className = 'block bg-white shadow';

                await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
                if (token !== renderToken) {
                    return;
                }
                canvases.push(canvas);
            }

            container.replaceChildren(...canvases);
        },
    };
};
